// App.js

import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Login from "./components/Login";
import Loading from "./components/Loading";
import PushNotificationToaster from "./components/PushNotificationToaster";
import ServiceTicketDesktopNotify from "./components/ServiceTicketDesktopNotify";
import JourneyDesktopNotify from "./components/JourneyDesktopNotify";
import InPageNotificationToast from "./components/InPageNotificationToast";
import NotificationErrorBoundary from "./components/NotificationErrorBoundary";
import { ChangePasswordModalProvider } from "./context/ChangePasswordModalContext";
import { RealtimeSocketProvider } from "./context/RealtimeSocketContext";
import ModuleRoute from "./components/ModuleRoute";
import $ from 'jquery';
import './AppShell.css';
import './styles/notifications.css';
import { prefetchRouteByPath, prefetchRouteByKey, routeLoaders } from './utils/routePrefetch';
import { testDesktopNotification } from './utils/webPush';
import { testSessionNotifications, simulateSessionInactivityFlow } from './utils/sessionNotificationTest';
import { testFirefoxNotifications, testFirefoxSimple } from './utils/firefoxNotificationTest';

window.$ = window.jQuery = $;
// Diagnóstico de notificaciones accesible desde la consola del navegador:
//   await window.__testNotificacion()
//   await window.__testSesionInactividad()
//   await window.__simularInactividad()
//   await window.__testFirefox()        // Diagnóstico completo Firefox
//   await window.__testFirefoxSimple()  // Test básico Firefox
//   window.__testInPageNotifications()  // Test notificaciones visuales
window.__testNotificacion = testDesktopNotification;
window.__testSesionInactividad = testSessionNotifications;
window.__simularInactividad = simulateSessionInactivityFlow;
window.__testFirefox = testFirefoxNotifications;
window.__testFirefoxSimple = testFirefoxSimple;

// Test de notificaciones visuales en la página
window.__testInPageNotifications = () => {
  console.log('🔔 Probando notificaciones visuales...');
  
  setTimeout(() => {
    window.showInPageNotification?.('Tareas pendientes', 'Hay 1 tarea en estado Pendiente en tu vista actual.', 'pending');
  }, 500);
  
  setTimeout(() => {
    window.showInPageNotification?.('Asignadas / reasignadas', 'Hay 2 tareas Asignadas o Reasignadas en tu vista actual.', 'assigned');
  }, 1500);
  
  setTimeout(() => {
    window.showInPageNotification?.('Reprogramación próxima o vencida', '1 reprogramada(s): fecha vencida o en las próximas 48 h. #12345 (10/4/2026 14:30).', 'urgent');
  }, 2500);
  
  setTimeout(() => {
    window.showInPageNotification?.('Éxito', 'Operación completada correctamente.', 'success');
  }, 3500);
  
  setTimeout(() => {
    window.showInPageNotification?.('Advertencia', 'Revisa la configuración antes de continuar.', 'warning');
  }, 4500);
};

// Función para limpiar logs de servicios
window.__clearServiceLogs = () => {
  if (window.loggedServices) {
    window.loggedServices.clear();
    console.log('🧹 Logs de servicios limpiados. Los servicios se volverán a loggear.');
  }
};

// Lazy loading para componentes pesados - mejora el rendimiento de navegación
const ResumenDashboard = lazy(routeLoaders.overview);
const Dashboard = lazy(routeLoaders.support);
const Report = lazy(routeLoaders.report);
const JourneyExperience = lazy(routeLoaders.journey);
const ServiceTickets = lazy(routeLoaders.serviceTickets);
const UserAdmin = lazy(routeLoaders.team);
const ProdClientesShell = lazy(routeLoaders.gestionClientes);
const SurveyForm = lazy(routeLoaders.surveyForm);
const SurveyClientRating = lazy(routeLoaders.surveyClientRating);
const PublicSurvey = lazy(routeLoaders.publicSurvey);
const ChangePasswordForm = lazy(routeLoaders.changePassword);

function AppRoutes() {
  const location = useLocation();

  useEffect(() => {
    prefetchRouteByPath(location.pathname);
    if (location.pathname === '/overview') {
      prefetchRouteByKey('support');
      prefetchRouteByKey('journey');
    } else if (location.pathname === '/support') {
      prefetchRouteByKey('overview');
      prefetchRouteByKey('report');
      prefetchRouteByKey('serviceTickets');
    } else if (location.pathname === '/team') {
      prefetchRouteByKey('overview');
      prefetchRouteByKey('support');
    }
  }, [location.pathname]);

  return (
    <div className="app-route-shell">
      <Suspense fallback={<div className="app-route-fallback"><Loading inline /></div>}>
        <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/overview" element={<ModuleRoute moduleKey="dashboard"><ResumenDashboard /></ModuleRoute>} />
        <Route path="/support" element={<ModuleRoute moduleKey="soporte"><Dashboard /></ModuleRoute>} />
        <Route path="/journey-experience" element={<ModuleRoute moduleKey="journey_experience"><JourneyExperience /></ModuleRoute>} />
        <Route path="/team" element={<ModuleRoute moduleKey="user_admin"><UserAdmin /></ModuleRoute>} />
        <Route path="/gestion-clientes/usuarios" element={<Navigate to="/team?tab=clientes-users" replace />} />
        <Route path="/gestion-clientes/*" element={<ModuleRoute moduleKey="gestion_clientes"><ProdClientesShell /></ModuleRoute>} />
        <Route path="/encuesta/:taskId/:encuestaId" element={<SurveyForm />} />
        <Route path="/encuesta-cliente" element={<SurveyClientRating />} />
        <Route path="/public/survey/:token" element={<PublicSurvey />} />
        <Route path="/report" element={<ModuleRoute moduleKey="dashboard_report"><Report /></ModuleRoute>} />
        <Route path="/service-tickets" element={<ModuleRoute moduleKey="service_tickets"><ServiceTickets /></ModuleRoute>} />
        <Route path="/change-password" element={<ChangePasswordForm />} />
        {/* Redirects from legacy URLs */}
        <Route path="/dashboard" element={<Navigate to="/overview" replace />} />
        <Route path="/resumen" element={<Navigate to="/overview" replace />} />
        <Route path="/soporte" element={<Navigate to="/support" replace />} />
        <Route path="/customer-journey" element={<Navigate to="/overview" replace />} />
        <Route path="/journey" element={<Navigate to="/overview" replace />} />
        <Route path="/user-admin" element={<Navigate to="/team" replace />} />
        <Route path="/usuarios" element={<Navigate to="/team" replace />} />
        </Routes>
      </Suspense>
    </div>
  );
}

function App() {
  return (
    <Router>
      <ChangePasswordModalProvider>
        <RealtimeSocketProvider>
        <PushNotificationToaster />
        <ServiceTicketDesktopNotify />
        <JourneyDesktopNotify />
        <NotificationErrorBoundary>
          <InPageNotificationToast />
        </NotificationErrorBoundary>
        <AppRoutes />
        </RealtimeSocketProvider>
      </ChangePasswordModalProvider>
    </Router>
  );
}

export default App;